import {
  Chain,
  delay,
  POI_REQUIRED_LISTS,
  BroadcasterConnectionStatus,
  SelectedBroadcaster,
  isDefined,
} from '@railgun-community/shared-models';
import { BroadcasterFeeCache } from './fees/broadcaster-fee-cache.js';
import { AddressFilter } from './filters/address-filter.js';
import {
  BroadcasterConnectionStatusCallback,
  BroadcasterDebugger,
  BroadcasterOptions,
} from './models/export-models.js';
import { BroadcasterConfig } from './models/broadcaster-config.js';
import { BroadcasterSearch } from './search/best-broadcaster.js';
import { BroadcasterStatus } from './status/broadcaster-connection-status.js';
import { BroadcasterDebug } from './utils/broadcaster-debug.js';
import { WakuObservers } from './waku/waku-observers.js';
import { WakuBroadcasterWakuCore } from './waku/waku-broadcaster-waku-core.js';
import type { LightNode } from '@waku/sdk';
import { contentTopics } from './waku/waku-topics.js';

export class WakuBroadcasterClient {
  private static chain: Chain;
  private static statusCallback: BroadcasterConnectionStatusCallback;
  private static started = false;
  private static isRestarting = false;

  static pollDelay = 3_000;

  static async start(
    chain: Chain,
    broadcasterOptions: BroadcasterOptions,
    statusCallback: BroadcasterConnectionStatusCallback,
    broadcasterDebugger?: BroadcasterDebugger,
  ) {
    this.chain = chain;
    this.statusCallback = statusCallback;

    WakuBroadcasterWakuCore.setBroadcasterOptions(broadcasterOptions);

    if (isDefined(broadcasterOptions.broadcasterVersionRange)) {
      BroadcasterConfig.MINIMUM_BROADCASTER_VERSION =
        broadcasterOptions.broadcasterVersionRange.minVersion;
      BroadcasterConfig.MAXIMUM_BROADCASTER_VERSION =
        broadcasterOptions.broadcasterVersionRange.maxVersion;
    }

    BroadcasterConfig.configurePeerConnections({
      useDNSDiscovery: broadcasterOptions.useDNSDiscovery,
      useCustomDNS: broadcasterOptions.useCustomDNS,
      dnsDiscoveryUrls: broadcasterOptions.dnsDiscoveryUrls,
      additionalDirectPeers: broadcasterOptions.additionalDirectPeers,
      additionalPeers: broadcasterOptions.additionalPeers,
      storePeers: broadcasterOptions.storePeers,
    });
    BroadcasterConfig.setHealthcheckLoggingEnabled(
      broadcasterOptions.enableHealthcheckLogs ?? false,
    );

    if (isDefined(broadcasterDebugger)) {
      BroadcasterDebug.setDebugger(broadcasterDebugger);
    }

    BroadcasterFeeCache.init(
      broadcasterOptions.poiActiveListKeys ??
      POI_REQUIRED_LISTS.map(list => list.key),
    );

    try {
      this.started = false;
      await WakuBroadcasterWakuCore.initWaku(chain);
      this.started = true;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.pollStatus();
    } catch (cause) {
      if (!(cause instanceof Error)) {
        throw new Error('Unexpected non-error thrown', { cause });
      }
      throw new Error('Cannot connect to Broadcaster network.', { cause });
    }
  }

  static async stop() {
    await WakuBroadcasterWakuCore.disconnect();
    this.started = false;
    this.updateStatus();
  }

  static isStarted() {
    return this.started;
  }

  static setHealthcheckLoggingEnabled(enabled: boolean) {
    BroadcasterConfig.setHealthcheckLoggingEnabled(enabled);
  }

  static async setChain(chain: Chain): Promise<void> {
    if (!WakuBroadcasterClient.started) {
      return;
    }

    WakuBroadcasterClient.chain = chain;
    await WakuObservers.setObserversForChain(
      WakuBroadcasterWakuCore.waku,
      chain,
    );
    WakuBroadcasterClient.updateStatus();
  }

  static getContentTopics(): string[] {
    return WakuObservers.getCurrentContentTopics();
  }

  static getMeshPeerCount(): number {
    return WakuBroadcasterWakuCore.getMeshPeerCount();
  }

  static getPubSubPeerCount(): number {
    return WakuBroadcasterWakuCore.getPubSubPeerCount();
  }

  static async getLightPushPeerCount(): Promise<number> {
    return await WakuBroadcasterWakuCore.getLightPushPeerCount();
  }

  static async getFilterPeerCount(): Promise<number> {
    return await WakuBroadcasterWakuCore.getFilterPeerCount();
  }
  /**
   * The function `findBestBroadcaster` finds the broadcaster with the lowest fees for a given chain and token.
   * @param {Chain} chain - The `chain` parameter is a Chain object that represents the network to find a broadcaster for.
   * @param {string} tokenAddress - The `tokenAddress` parameter is a string that represents the
   * address of an ERC20 Token on the network, a broadcaster broadcasting fees for this token will be selected.
   * @param {boolean} useRelayAdapt - A boolean value indicating whether to select broadcasters that
   * support RelayAdapt transactions.
   * @returns an Optional<SelectedBroadcaster> object.
   */
  static findBestBroadcaster(
    chain: Chain,
    tokenAddress: string,
    useRelayAdapt: boolean,
  ): Optional<SelectedBroadcaster> {
    if (!WakuBroadcasterClient.started) {
      return;
    }

    return BroadcasterSearch.findBestBroadcaster(
      chain,
      tokenAddress,
      useRelayAdapt,
    );
  }

  /**
   * The function `findAllBroadcastersForChain` returns an array of all available broadcasters fee-tokens for a given chain.
   * @param {Chain} chain - The `chain` parameter is a Chain object that represents the network to find all broadcasters for.
   * @param {boolean} useRelayAdapt - A boolean value indicating whether to select broadcasters that
   * support RelayAdapt transactions.
   * @returns an Optional<SelectedBroadcaster[]> object.
   */
  static findAllBroadcastersForChain(
    chain: Chain,
    useRelayAdapt: boolean,
  ): Optional<SelectedBroadcaster[]> {
    if (!WakuBroadcasterClient.started) {
      return [];
    }

    return BroadcasterSearch.findAllBroadcastersForChain(chain, useRelayAdapt);
  }

  /**
   * The function `findRandomBroadcasterForToken` selects a random broadcaster from a list of broadcasters that is based on
   * their fees for a specific token, and how much higher their fees are compared to the broadcaster with
   * the lowest fees.
   * @param {Chain} chain - The `chain` parameter is a Chain object that represents the network to find a broadcaster for.
   * @param {string} tokenAddress - The `tokenAddress` parameter is a string that represents the
   * address of an ERC20 Token on the network, a broadcaster broadcasting fees for this token will be selected.
   * @param {boolean} useRelayAdapt - A boolean value indicating whether to select broadcasters that
   * support RelayAdapt transactions.
   * @param {number} [percentageThreshold=5] - The `percentageThreshold` parameter is a number that
   * represents the maximum percentage increase in fees that a broadcaster can have compared to the broadcaster
   * with the lowest fees. For example, if the `percentageThreshold` is set to 5, it means that a
   * broadcaster can have a maximum of 5% higher fees than the broadcaster with the lowest fees and still be selected.
   * Defaults to 5.
   * @returns an Optional<SelectedBroadcaster> object.
   */
  static findRandomBroadcasterForToken(
    chain: Chain,
    tokenAddress: string,
    useRelayAdapt: boolean,
    percentageThreshold: number = 5,
  ): Optional<SelectedBroadcaster> {
    if (!WakuBroadcasterClient.started) {
      return;
    }

    return BroadcasterSearch.findRandomBroadcasterForToken(
      chain,
      tokenAddress,
      useRelayAdapt,
      percentageThreshold,
    );
  }

  /**
   * The function `findBroadcastersForToken` takes in a chain, token address, and a boolean flag, and
   * returns an array of selected broadcasters based on the provided parameters.
   * @param {Chain} chain - The `chain` parameter is a Chain object that represents the network to find a broadcaster for.
   * @param {string} tokenAddress - The `tokenAddress` parameter is a string that represents the
   * address of an ERC20 Token on the network; a broadcaster broadcasting fees for this token will be selected.
   * @param {boolean} useRelayAdapt - A boolean value indicating whether to select broadcasters that
   * support RelayAdapt transactions.
   * @returns an Optional<SelectedBroadcaster[]> object.
   */
  static findBroadcastersForToken(
    chain: Chain,
    tokenAddress: string,
    useRelayAdapt: boolean,
  ): Optional<SelectedBroadcaster[]> {
    if (!WakuBroadcasterClient.started) {
      return;
    }

    return BroadcasterSearch.findBroadcastersForToken(
      chain,
      tokenAddress,
      useRelayAdapt,
    );
  }

  static setAddressFilters(
    allowlist: Optional<string[]>,
    blocklist: Optional<string[]>,
  ): void {
    AddressFilter.setAllowlist(allowlist);
    AddressFilter.setBlocklist(blocklist);
  }

  static async tryReconnect(): Promise<void> {
    // Reset fees, which will reset status to "Searching".
    BroadcasterFeeCache.resetCache(WakuBroadcasterClient.chain);
    WakuBroadcasterClient.updateStatus();

    await WakuBroadcasterClient.restart();
  }

  static supportsToken(
    chain: Chain,
    tokenAddress: string,
    useRelayAdapt: boolean,
  ) {
    return BroadcasterFeeCache.supportsToken(
      chain,
      tokenAddress,
      useRelayAdapt,
    );
  }

  private static async restart(): Promise<void> {
    if (this.isRestarting || !this.started) {
      return;
    }
    this.isRestarting = true;
    try {
      BroadcasterDebug.log('Restarting Waku...');
      await WakuBroadcasterWakuCore.reinitWaku(this.chain);
      this.isRestarting = false;
    } catch (cause) {
      this.isRestarting = false;
      if (!(cause instanceof Error)) {
        return;
      }
      BroadcasterDebug.error(
        new Error('Error reinitializing Waku Broadcaster Client', { cause }),
      );
    }
  }

  /**
   * Start keep-alive poller which checks Broadcaster status every few seconds.
   */
  private static async pollStatus(): Promise<void> {
    const status = this.updateStatus();
    if (BroadcasterConfig.enableHealthcheckLogs) {
      await this.logHealthcheck(status);
    }
    await delay(WakuBroadcasterClient.pollDelay);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.pollStatus();
  }

  private static updateStatus(): BroadcasterConnectionStatus {
    const status = BroadcasterStatus.getBroadcasterConnectionStatus(this.chain);

    this.statusCallback(this.chain, status);
    if (
      status === BroadcasterConnectionStatus.Disconnected ||
      status === BroadcasterConnectionStatus.Error
    ) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.restart();
    }

    return status;
  }

  private static formatHealthcheckLog(healthSnapshot: any): string {
    const discovery = healthSnapshot.discovery ?? {};
    const peerStore = discovery.peerStore ?? {};
    const connectedPeerDetails = discovery.connectedPeerDetails ?? [];

    const lines = [
      'Waku healthcheck:',
      `  status: ${healthSnapshot.status}`,
      `  chain: ${healthSnapshot.chain}`,
      `  started: ${healthSnapshot.started}, restarting: ${healthSnapshot.isRestarting}, wakuStarted: ${healthSnapshot.isStarted}, error: ${healthSnapshot.hasError}`,
      `  routing: cluster=${healthSnapshot.routing.clusterId}, shard=${healthSnapshot.routing.shardId}, topic=${healthSnapshot.routing.pubsubTopic}`,
      `  configured peers: dns=${healthSnapshot.configuredPeers.dnsDiscoveryUrls.length}, bootstrap=${healthSnapshot.configuredPeers.bootstrapPeers.length}, store=${healthSnapshot.configuredPeers.storePeers.length}`,
      `  connections: ${healthSnapshot.connections.count} / peers=${healthSnapshot.connections.peers.join(', ') || 'none'}`,
      `  peer store: total=${peerStore.count ?? 0}, bootstrap=${peerStore.bootstrapCount ?? 0}, peer-exchange=${peerStore.peerExchangeCount ?? 0}`,
      `  connected peer-exchange support: ${peerStore.connectedPeersSupportingPeerExchange?.join(', ') || 'none'}`,
      `  content topics: ${healthSnapshot.contentTopics.join(', ') || 'none'}`,
    ];

    if (connectedPeerDetails.length) {
      lines.push('  connected peer details:');
      for (const peer of connectedPeerDetails) {
        lines.push(
          `    - ${peer.peerId} | px=${peer.supportsPeerExchange} | tags=${peer.tags.join(', ') || 'none'} | protocols=${peer.protocols.join(', ') || 'none'}`,
        );
      }
    }

    return lines.join('\n');
  }

  private static async logHealthcheck(status: BroadcasterConnectionStatus) {
    const chain = this.chain
      ? `${this.chain.type}:${this.chain.id}`
      : 'undefined';
    const healthSnapshot = {
      pollDelay: this.pollDelay,
      started: this.started,
      isRestarting: this.isRestarting,
      chain,
      status,
      ...await WakuBroadcasterWakuCore.getHealthSnapshot(),
    };

    BroadcasterDebug.log(this.formatHealthcheckLog(healthSnapshot));
  }

  // Waku Transport functions
  static async addTransportSubscription(
    waku: Optional<LightNode>,
    topic: string,
    callback: (message: any) => void,
  ): Promise<void> {
    await WakuObservers.addTransportSubscription(
      WakuBroadcasterWakuCore.waku,
      topic,
      callback,
    );
  }

  static async sendTransport(data: object, topic: string): Promise<void> {
    const customTopic = contentTopics.encrypted(topic);
    try {

      await WakuBroadcasterWakuCore.broadcastMessage(data, customTopic);
    } catch (e) {
      BroadcasterDebug.log(
        `SendTransport error: ${e.message}`
      )
    }
  }

  static getWakuCore(): Optional<LightNode> {
    return WakuBroadcasterWakuCore.waku;
  }

  static setRestartCallback(callback: () => void): void {
    WakuBroadcasterWakuCore.setWakuRestartCallback(callback);
  }
}
